import type { AppErrorKind } from '@/lib/telemetry/faro';

/**
 * Payment error classification for telemetry.
 *
 * Mirrors `firebase-errors.ts`: an explicit expected-set, an explicit systemic-set, and
 * anything in neither is `unclassified` and still reported — dropping the unknown is
 * exactly how two auth alerts shipped dead in Aug 2026.
 *
 * The load-bearing rule: a DECLINED CARD IS NOT AN OUTAGE. The backend measured
 * card_declined 234, customer_tax_location_invalid 102, insufficient_funds 66 and
 * requires_action 4 over 7 days — 406 events versus 98 for every real failure combined.
 * Reporting those as systemic would make the payment signal ~80% noise, the same mistake
 * `auth/network-request-failed` made at 76%.
 */

/**
 * Code sets from the official Stripe error-codes reference (https://docs.stripe.com/error-codes),
 * kept deliberately parallel to the backend's `payment-failure.log.ts`. They are duplicated
 * because the two live in different repos — if you change one, change the other.
 */

/**
 * The customer's own card, address, or bank. Never reported.
 * Backend measured 406 of these in 7 days vs 98 real failures.
 */
const EXPECTED_PAYMENT_CODES = new Set([
  // card rejected / unusable
  'card_declined',
  'card_decline_rate_limit_exceeded',
  'expired_card',
  'incorrect_cvc',
  'invalid_cvc',
  'incorrect_number',
  'invalid_number',
  'invalid_expiry_month',
  'invalid_expiry_year',
  'insufficient_funds',
  // Stripe Elements' own incomplete-field codes, which fire while the user is still typing
  'incomplete_number',
  'incomplete_cvc',
  'incomplete_expiry',
  'incomplete_zip',
  // address / tax location
  'customer_tax_location_invalid',
  'invalid_tax_location',
  'incorrect_zip',
  'postal_code_invalid',
  'incorrect_address',
  'tax_id_invalid',
  'email_invalid',
  // 3DS/SCA the user must complete or abandoned — control flow, not failure
  'authentication_required',
  'requires_action',
  'payment_intent_authentication_failure',
  'setup_intent_authentication_failure',
  'payment_intent_action_required',
  // issuer or customer refused
  'payment_method_provider_decline',
  'payment_method_customer_decline',
  'payment_intent_payment_attempt_failed',
  'refer_to_customer',
  'debit_not_authorized',
  'account_closed',
  'bank_account_declined',
  // promo the customer entered
  'coupon_expired',
  'promotion_code_customer_not_first_time',
  // Provider-transient. Silent HERE on purpose: the server sees these too and owns the
  // aggregate view, so reporting them from the browser duplicates noise without adding
  // information. The user already sees a retry prompt.
  'processing_error',
  'payment_method_not_available',
  'payment_method_provider_timeout',
  'rate_limit',
  'lock_timeout',
]);

/**
 * OUR Stripe configuration is broken — these fail for EVERY customer. Semantically this is
 * exactly what the existing `config_regression` kind means, but `classifyPaymentError` below
 * deliberately returns `systemic` for this set instead — see the comment there for why
 * (logs-stack-v2's `FirebaseAuthConfigRegression` alert has no `evt` filter). The set itself
 * still exists so `code` (api_key_expired etc.) is preserved for whoever writes the payment
 * alert rules.
 */
const CONFIG_REGRESSION_PAYMENT_CODES = new Set([
  'api_key_expired',
  'secret_key_required',
  'account_invalid',
  'livemode_mismatch',
  'testmode_charges_only',
  'payment_method_unactivated',
  'stripe_tax_inactive',
  'tls_version_unsupported',
]);

/** Codes meaning WE are broken in a non-config way. */
const SYSTEMIC_PAYMENT_CODES = new Set([
  'api_error',
  'api_connection_error',
  'resource_missing', // we referenced a Stripe object that no longer exists
  'parameter_missing',
  'parameter_unknown',
  'parameter_invalid_empty',
  'invalid_charge_amount',
  'amount_too_large',
  'amount_too_small',
  'payment_intent_invalid_parameter',
  'setup_intent_invalid_parameter',
  'payment_method_invalid_parameter',
  'payment_intent_unexpected_state',
  'setup_intent_unexpected_state',
  'intent_invalid_state',
  'idempotency_key_in_use',
  'token_already_used',
  // NOT the customer's wallet — OUR Stripe balance is too low for a transfer/payout.
  'balance_insufficient',
]);

/**
 * Application codes returned by open-api in `PaymentStatusResponse.code`.
 *
 * A recognised value here means THE BACKEND ALREADY OWNS THIS SIGNAL, so the browser stays
 * quiet. They are all either the customer's own problem (declined card, bad postal code,
 * unpaid invoice) or an app-level guard — and for `ROLLBACK`, the one genuinely
 * ours-to-fix case, open-api already emits `charge_failed_reverted` WITH accountId and the
 * Stripe error fields. Re-reporting from here would add noise and no information.
 *
 * Numeric values from `PAYMENT_STATUS_CODE` (open-api `shared/common-defs.ts`); the string
 * names appear because some paths surface the key rather than the number.
 */
const BACKEND_OWNED_PAYMENT_CODES = new Set<string>([
  '1010',
  'ZIPCODE_ISSUE',
  '1001',
  'PAYMENT_FAILED',
  '5555',
  'LIMIT_QUANTITY',
  '5556',
  'ROLLBACK',
  '5557',
  'UNPAID_INVOICE',
  '6000',
  'DOWN_GRADE_TRIAL_USER',
  '6001',
  'SYNC_PRICE_STRIPE',
  'STRIPE_FAILED',
]);

/**
 * Read a payment error code, defensively, from any of the shapes this surface produces: a
 * numeric application code, a string application code name, a raw Stripe string code (flat
 * or nested under `error`), or — Apollo Client 4 — a GraphQL business error's `extensions.code`
 * (`CombinedGraphQLErrors.graphQLErrors[0].extensions.code`) or a network failure's
 * `networkError.statusCode`.
 *
 * Numbers are coerced to string. A reader that only accepts `typeof code === 'string'`
 * silently drops every numeric application code — which would classify every
 * bad-postal-code rejection as `systemic`.
 */
function paymentErrorCode(err: unknown): string {
  try {
    if (!err || typeof err !== 'object') return '';
    const e = err as {
      code?: unknown;
      error?: { code?: unknown };
      graphQLErrors?: Array<{ extensions?: { code?: unknown } }>;
      networkError?: { statusCode?: unknown };
    };
    if (typeof e.code === 'string') return e.code;
    if (typeof e.code === 'number') return String(e.code);
    if (typeof e.error?.code === 'string') return e.error.code;
    if (typeof e.error?.code === 'number') return String(e.error.code);
    // Apollo Client 4: GraphQL business errors reject as CombinedGraphQLErrors, carrying
    // their code in extensions rather than a top-level `code`.
    const gqlCode = e.graphQLErrors?.[0]?.extensions?.code;
    if (typeof gqlCode === 'string') return gqlCode;
    // AC4 network failures are ServerError/ServerParseError (plain Errors with no top-level
    // code), but a statusCode may still be present on some shapes.
    if (typeof e.networkError?.statusCode === 'number') return `http_${e.networkError.statusCode}`;
    return '';
  } catch {
    return '';
  }
}

/**
 * True for codes we deliberately never report — the customer's own card/address, or a
 * code the backend already owns.
 */
export function isExpectedPaymentError(code: string | undefined): boolean {
  if (!code) return false;
  // Stripe codes are lowercase and matched exactly; our own application codes are
  // emitted in both cases depending on the call site (e.g. AddonUpdateDialog compares
  // lowercase 'payment_failed'), so match those case-insensitively. .toUpperCase() leaves
  // the numeric string forms ('1010') unchanged.
  return EXPECTED_PAYMENT_CODES.has(code) || BACKEND_OWNED_PAYMENT_CODES.has(code.toUpperCase());
}

/**
 * The code to attach to telemetry. Lives here (not in faro.ts) because it needs the
 * private `paymentErrorCode` reader above.
 */
export function paymentErrorCodeForTelemetry(err: unknown): string {
  return paymentErrorCode(err) || 'no-code';
}

/**
 * Classify a caught payment error. `'expected'` means do not report at all.
 *
 * A thrown non-Stripe, non-GraphQL error (e.g. Stripe.js failed to load, so `Stripe` is
 * undefined) has no code and IS our problem, so it classifies systemic. A GraphQL business
 * rejection (Apollo Client 4's `CombinedGraphQLErrors`) is also an `Error` but is usually a
 * routine rejection (e.g. an invalid promo code) — see the `!code` branch below.
 */
export function classifyPaymentError(err: unknown): AppErrorKind | 'expected' {
  const code = paymentErrorCode(err);
  // config_regression codes first — a broken key must never be masked as "expected".
  //
  // Deliberately return 'systemic', NOT 'config_regression', even though that is what
  // these codes semantically are.
  //
  // logs-stack-v2 ruler-rules.yaml:423 (FirebaseAuthConfigRegression, severity critical,
  // pages) selects `{app="scio3-web", env="prod"} | event_data_kind = config_regression`
  // with NO evt filter — unlike every other scio3-web rule, which pins
  // event_data_evt = auth_error. Emitting config_regression from a payment path would
  // therefore page the AUTH on-call for a Stripe key problem.
  //
  // TO RESTORE the more accurate kind: first add `event_data_evt = auth_error` to that
  // rule, then change this back to 'config_regression'. Until then the distinction lives
  // in `code` (api_key_expired etc.), which is preserved either way.
  if (CONFIG_REGRESSION_PAYMENT_CODES.has(code)) return 'systemic';
  if (isExpectedPaymentError(code)) return 'expected';
  if (SYSTEMIC_PAYMENT_CODES.has(code)) return 'systemic';
  // Apollo Client 4 network failure (ServerError/ServerParseError) — always OUR outage,
  // regardless of whether `paymentErrorCode` could read a `networkError.statusCode` (that
  // reader feeds telemetry's `code` field, not this decision — an unrecognised `http_5xx`
  // string must not fall through to `unclassified` below).
  const isNetworkFailure =
    !!err &&
    typeof err === 'object' &&
    'networkError' in err &&
    !!(err as { networkError?: unknown }).networkError;
  if (isNetworkFailure) return 'systemic';
  // No code at all. Only OUR failure if it is a genuine transport/JS exception.
  //
  // Apollo Client 4 rejects GraphQL business errors with CombinedGraphQLErrors — an Error
  // subclass with no top-level code and a `graphQLErrors` array. Treating "is an Error" as
  // "our outage" made a bad promo code (useSubscriptionWizard's applyPromoCode catch) look
  // like one. A GraphQL rejection with no recognised extensions.code is a business
  // rejection, not an outage. A plain {status:false, message} response is not even an
  // Error and was already `unclassified`.
  if (!code) {
    const isGraphQLRejection = !!err && typeof err === 'object' && 'graphQLErrors' in err;
    if (isGraphQLRejection) return 'unclassified';
    return err instanceof Error ? 'systemic' : 'unclassified';
  }
  return 'unclassified';
}
